import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import axios from 'axios';

import { IInspectionItemPerformed, defaultValue } from 'app/shared/model/inspection-item-performed.model';
import { EntityState, IQueryParams, createEntitySlice, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { cleanEntity } from 'app/shared/util/entity-utils';

const initialState: EntityState<IInspectionItemPerformed> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/inspection-item-performeds';

// Actions

export const getEntities = createAsyncThunk('inspectionItemPerformed/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<IInspectionItemPerformed[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'inspectionItemPerformed/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IInspectionItemPerformed>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'inspectionItemPerformed/create_entity',
  async (entity: IInspectionItemPerformed, thunkAPI) => {
    const result = await axios.post<IInspectionItemPerformed>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'inspectionItemPerformed/update_entity',
  async (entity: IInspectionItemPerformed, thunkAPI) => {
    const result = await axios.put<IInspectionItemPerformed>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'inspectionItemPerformed/partial_update_entity',
  async (entity: IInspectionItemPerformed, thunkAPI) => {
    const result = await axios.patch<IInspectionItemPerformed>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'inspectionItemPerformed/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IInspectionItemPerformed>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const getAllInspectionItemPerformedsByHistoryId = createAsyncThunk(
  'inspectionItemPerformed/fetch_entity_list_by_history_id',
  async (historyId: string | number) => {
    const requestUrl = `${apiUrl}/history/${historyId}`;
    return axios.get<IInspectionItemPerformed[]>(requestUrl);
  }
);

// slice

export const InspectionItemPerformedSlice = createEntitySlice({
  name: 'inspectionItemPerformed',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getAllInspectionItemPerformedsByHistoryId), (state, action) => {
        state.loading = false;
        state.entities = action.payload.data;
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getAllInspectionItemPerformedsByHistoryId, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = InspectionItemPerformedSlice.actions;

// Reducer
export default InspectionItemPerformedSlice.reducer;
